import { useState, useMemo } from 'react'

export type Step = 'hook' | 'diagnostic' | 'email' | 'result'
export type Tier = 'control' | 'clarity' | 'risk'

interface Question {
  id: number
  text: string
  options: {
    text: string
    points: number
  }[]
}

// Scoring: Always = 3, Mostly = 2, Sometimes = 1, Rarely = 0
// Q5 is inverted: Always (nodding along) = 0, Rarely = 3
const QUESTIONS: Question[] = [
  {
    id: 1,
    text: 'When work is shown, do you understand why it\u2019s been done that way?',
    options: [
      { text: 'Always', points: 3 },
      { text: 'Mostly', points: 2 },
      { text: 'Sometimes', points: 1 },
      { text: 'Rarely', points: 0 },
    ],
  },
  {
    id: 2,
    text: 'If a project runs over time or budget, do you usually know why?',
    options: [
      { text: 'Always', points: 3 },
      { text: 'Mostly', points: 2 },
      { text: 'Sometimes', points: 1 },
      { text: 'Rarely', points: 0 },
    ],
  },
  {
    id: 3,
    text: 'Do your projects have clear goals everyone agrees on?',
    options: [
      { text: 'Always', points: 3 },
      { text: 'Mostly', points: 2 },
      { text: 'Sometimes', points: 1 },
      { text: 'Rarely', points: 0 },
    ],
  },
  {
    id: 4,
    text: 'Do you feel comfortable pushing back in meetings?',
    options: [
      { text: 'Always', points: 3 },
      { text: 'Mostly', points: 2 },
      { text: 'Sometimes', points: 1 },
      { text: 'Rarely', points: 0 },
    ],
  },
  {
    id: 5,
    text: 'Have you ever left a meeting unsure, but nodded along anyway?',
    options: [
      { text: 'Always', points: 0 },
      { text: 'Mostly', points: 1 },
      { text: 'Sometimes', points: 2 },
      { text: 'Rarely', points: 3 },
    ],
  },
]

// Max score: 15, Min: 0
// Tier 1 (High Control): 11-15
// Tier 2 (Moderate Control): 6-10
// Tier 3 (Low Control): 0-5

export function useDiagnostic() {
  const [step, setStep] = useState<Step>('hook')
  const [questionIndex, setQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<number[]>([])
  const [email, setEmail] = useState('')

  const currentQuestion = QUESTIONS[questionIndex]
  const totalQuestions = QUESTIONS.length

  const score = useMemo(() => answers.reduce((a, b) => a + b, 0), [answers])

  const resultTier: Tier = useMemo(() => {
    if (score >= 11) return 'control'
    if (score >= 6) return 'clarity'
    return 'risk'
  }, [score])

  const startDiagnostic = () => {
    setStep('diagnostic')
    setQuestionIndex(0)
    setAnswers([])
  }

  const answerQuestion = (points: number) => {
    const newAnswers = [...answers, points]
    setAnswers(newAnswers)

    if (questionIndex < totalQuestions - 1) {
      setQuestionIndex((prev) => prev + 1)
    } else {
      setStep('email')
    }
  }

  const submitEmail = (emailAddress: string) => {
    setEmail(emailAddress)

    // TODO: Send to Attio CRM via webhook
    // Create or update contact in Attio
    console.log('Attio webhook payload:', {
      email: emailAddress,
      tag: 'UX Guide Lead',
      diagnosticTier: resultTier,
      diagnosticScore: score,
      source: 'ux-guide',
    })

    setStep('result')
  }

  return {
    step,
    currentQuestion,
    questionIndex,
    totalQuestions,
    score,
    startDiagnostic,
    answerQuestion,
    submitEmail,
    resultTier,
  }
}
